import React, { useState } from 'react';
import { addDays } from 'date-fns';
import { Casa } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { useAuth } from '../../context/AuthContext';
import { usePerfil } from '../../context/PerfilContext';
import { useNavigate } from 'react-router-dom';
import { Ocurrencia, describirDia, describirRepeticion, siguienteEnTurno, turnoVigente, yoEn } from '../../utils/tareas';
import { CalendarDays, Check, Edit2, Receipt, SkipForward, Undo2 } from 'lucide-react';
import { MensajeError, ModalBase, aInput, botonPrincipal, botonSecundario, chip, claseInput, deInput } from './ui';
import { vibrar } from '../../utils/hapticos';

interface Props {
  o: Ocurrencia;
  casa?: Casa;
  onEditar: () => void;
  onClose: () => void;
}

/**
 * Lo que se puede hacer con una tarea de la semana: marcarla (por ti o por
 * otra persona de la casa), saltarla esta vez, moverla a otro día o editarla.
 * Si ya está hecha, deshacerlo.
 */
export const ModalAccionesTarea: React.FC<Props> = ({ o, casa, onEditar, onClose }) => {
  const { hechas, marcarHecha, posponer, deshacer } = useTareas();
  const { user } = useAuth();
  const { tieneModulo } = usePerfil();
  const navigate = useNavigate();
  const { tarea, estado, hecha } = o;
  const yo = yoEn(casa, user?.uid);
  const [quien, setQuien] = useState(yo?.id ?? tarea.asignadoA ?? '');
  const [otroDia, setOtroDia] = useState(aInput(addDays(new Date(), 1)));
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const hacer = async (fn: () => Promise<void>, vibracion: () => void = vibrar.exito) => {
    setOcupado(true);
    setError('');
    try {
      await fn();
      vibracion();
      onClose();
    } catch (e) {
      console.error(e);
      vibrar.error();
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setOcupado(false);
    }
  };

  // Deshacer devuelve la tarea a su fecha de antes: solo tiene sentido con la última vez
  const esLaUltima = !!hecha && !hechas.some(h =>
    h.tareaId === hecha.tareaId && h.casaId === hecha.casaId && h.hechaEn > hecha.hechaEn);
  const personas = (casa?.participantes ?? []).filter(p => p.activo || p.id === quien);
  const nombre = (id?: string) => casa?.participantes.find(p => p.id === id)?.nombre;

  return (
    <ModalBase titulo={`${tarea.icono} ${tarea.nombre}`} onClose={onClose}>
      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {[
            describirRepeticion(tarea.repeticion, tarea.fecha),
            tarea.zona,
            !casa?.personal && (tarea.asignadoA ? `Le toca a ${nombre(tarea.asignadoA) ?? 'alguien que ya no está'}` : 'La puede hacer cualquiera'),
            turnoVigente(tarea.turno, casa).length > 1
              && `por turnos, luego ${nombre(siguienteEnTurno(turnoVigente(tarea.turno, casa), tarea.asignadoA))}`,
          ].filter(Boolean).join(' · ')}
        </p>
        {tarea.notas && (
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line bg-gray-50 dark:bg-gray-900/40 rounded-lg px-3 py-2">
            {tarea.notas}
          </p>
        )}
        <MensajeError texto={error} />

        {hecha ? (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {hecha.saltada ? 'Se saltó' : 'Hecha'} {describirDia(hecha.hechaEn).toLowerCase()}
              {hecha.participanteId && !casa?.personal ? ` por ${nombre(hecha.participanteId) ?? 'alguien'}` : ''}.
            </p>
            {esLaUltima ? (
              <button disabled={ocupado} onClick={() => hacer(() => deshacer(hecha), vibrar.fuerte)}
                className={`w-full py-2.5 ${botonSecundario}`}>
                <Undo2 className="w-4 h-4" /> Deshacer
              </button>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Ya se ha vuelto a hacer: solo se puede deshacer la última vez.</p>
            )}
          </>
        ) : estado === 'futura' ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Esta vez toca {describirDia(o.fecha).toLowerCase()}. Antes hay que hacer la de {describirDia(tarea.fecha).toLowerCase()}.
          </p>
        ) : (
          <>
            {!casa?.personal && personas.length > 1 && (
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">¿Quién la ha hecho?</p>
                <div className="flex flex-wrap gap-1.5">
                  {personas.map(p => (
                    <button key={p.id} type="button" onClick={() => setQuien(p.id)} className={chip(quien === p.id)}>
                      {p.nombre}{p.id === yo?.id ? ' (yo)' : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button disabled={ocupado} onClick={() => hacer(() => marcarHecha(tarea, quien || undefined))}
              className={`w-full py-2.5 ${botonPrincipal}`}>
              <Check className="w-4 h-4" /> Hecha
            </button>
            {/* La compra, el butano, la ITV…: hecha y, de paso, el gasto en Finanzas con el nombre puesto */}
            {tieneModulo('finanzas') && (
              <button disabled={ocupado}
                onClick={() => hacer(async () => {
                  await marcarHecha(tarea, quien || undefined);
                  navigate(`/apuntar?nota=${encodeURIComponent(tarea.nombre)}`);
                })}
                className={`w-full py-2 ${botonSecundario}`}>
                <Receipt className="w-4 h-4" /> Hecha y apuntar lo que ha costado
              </button>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button disabled={ocupado} onClick={() => hacer(() => marcarHecha(tarea, quien || undefined, true), vibrar.fuerte)}
                className={`py-2 ${botonSecundario}`}
                title={tarea.repeticion.tipo === 'una' ? 'No se hará: queda cerrada' : 'Esta vez no: pasa a la siguiente'}>
                <SkipForward className="w-4 h-4" /> Saltar
              </button>
              <button disabled={ocupado} onClick={() => hacer(() => posponer(tarea, addDays(new Date(), 1)))}
                className={`py-2 ${botonSecundario}`}>
                <CalendarDays className="w-4 h-4" /> Mañana
              </button>
            </div>

            <div className="flex gap-2 items-center">
              <input type="date" value={otroDia} onChange={e => setOtroDia(e.target.value)} className={claseInput}
                aria-label="Mover a otro día" />
              <button disabled={ocupado || !otroDia} onClick={() => hacer(() => posponer(tarea, deInput(otroDia)!))}
                className={`shrink-0 py-2 ${botonSecundario}`}>
                Mover
              </button>
            </div>
          </>
        )}

        {!hecha && (
          <button onClick={onEditar}
            className="inline-flex items-center gap-1 text-sm font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
            <Edit2 className="w-4 h-4" /> Editar la tarea
          </button>
        )}
      </div>
    </ModalBase>
  );
};
